import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { 
  Brain, Sparkles, TrendingUp, DollarSign, AlertTriangle, 
  RefreshCw, Check, X, ChevronRight, Lightbulb, BarChart3,
  Target, Shield, Zap
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { useCurrency } from '../utils/currencyUtils';

const ScenarioOptimizerPage = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, currencySymbol } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Constraints form
  const [constraints, setConstraints] = useState({
    budget_cap: 0,
    benefit_target: null,
    resource_availability: null,
    risk_threshold: 'medium',
    min_allocation_per_item: 0,
    max_allocation_per_item: null
  });
  const [optimizationGoal, setOptimizationGoal] = useState('balanced');

  useEffect(() => {
    if (scenarioId) {
      fetchScenario();
      fetchHistory();
    }
  }, [scenarioId]);

  const fetchScenario = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/scenarios/${scenarioId}`);
      setScenario(response.data);
      setConstraints(prev => ({
        ...prev,
        budget_cap: response.data.total_envelope || 0,
        max_allocation_per_item: response.data.total_envelope || 0
      }));
    } catch (error) {
      console.error('Error fetching scenario:', error);
      toast.error('Failed to load scenario');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get(`/scenarios/optimizer/history/${scenarioId}`);
      setHistory(response.data.optimizations || []);
    } catch (error) {
      console.debug('No optimization history');
    }
  };

  const handleOptimize = async () => {
    try {
      setOptimizing(true);
      const response = await api.post('/scenarios/optimizer/optimize', {
        scenario_id: scenarioId,
        constraints: {
          budget_cap: constraints.budget_cap,
          benefit_target: constraints.benefit_target,
          resource_availability: constraints.resource_availability,
          risk_threshold: constraints.risk_threshold,
          min_allocation_per_item: constraints.min_allocation_per_item,
          max_allocation_per_item: constraints.max_allocation_per_item
        },
        optimization_goal: optimizationGoal,
        include_rationale: true
      });
      setResult(response.data);
      toast.success('Optimization complete!');
      fetchHistory();
    } catch (error) {
      console.error('Optimization error:', error);
      toast.error('Optimization failed');
    } finally {
      setOptimizing(false);
    }
  };

  const handleApply = async () => {
    if (!result?.optimization_id) return;
    
    try {
      setApplying(true);
      await api.post(`/scenarios/optimizer/apply/${result.optimization_id}`);
      toast.success('Optimization applied to scenario');
      navigate(`/scenarios/${scenarioId}`);
    } catch (error) {
      console.error('Error applying optimization:', error);
      toast.error('Failed to apply optimization');
    } finally {
      setApplying(false);
    }
  };

  // formatCurrency now comes from useCurrency hook

  const getGoalIcon = (goal) => {
    switch (goal) {
      case 'maximize_roi':
        return <TrendingUp className="w-5 h-5" />;
      case 'minimize_risk':
        return <Shield className="w-5 h-5" />;
      case 'maximize_benefit':
        return <Target className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  const getDeltaColor = (delta) => {
    if (delta > 0) return 'text-green-600';
    if (delta < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Brain className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Scenario Optimizer
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            AI-powered allocation optimization for {scenario?.name || 'Scenario'}
          </p>
        </div>
        {result && (
          <div className="flex items-center gap-2">
            <Badge 
              variant={result.ai_generated ? 'default' : 'secondary'}
              className="flex items-center gap-1"
            >
              {result.ai_generated ? <Sparkles className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
              {result.ai_generated ? 'AI Generated' : 'Heuristic'}
            </Badge>
            <Badge variant="outline">
              Confidence: {Math.round((result.confidence_score || 0) * 100)}%
            </Badge>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Constraints Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Optimization Constraints
            </CardTitle>
            <CardDescription>Configure bounds for the optimizer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Budget Cap */}
            <div>
              <Label>Budget Cap</Label>
              <Input
                type="number"
                value={constraints.budget_cap}
                onChange={(e) => setConstraints(prev => ({ ...prev, budget_cap: Number(e.target.value) }))}
                data-testid="budget-cap"
              />
              <p className="text-xs text-[var(--clr-text-secondary)] mt-1">
                Maximum total allocation
              </p>
            </div>

            {/* Benefit Target */}
            <div>
              <Label>Benefit Target (optional)</Label>
              <Input
                type="number"
                value={constraints.benefit_target || ''}
                onChange={(e) => setConstraints(prev => ({ ...prev, benefit_target: e.target.value ? Number(e.target.value) : null }))}
                placeholder="Minimum expected benefit"
                data-testid="benefit-target"
              />
            </div>

            {/* Risk Threshold */}
            <div>
              <Label>Risk Threshold</Label>
              <Select 
                value={constraints.risk_threshold} 
                onValueChange={(v) => setConstraints(prev => ({ ...prev, risk_threshold: v }))}
              >
                <SelectTrigger data-testid="risk-threshold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Conservative</SelectItem>
                  <SelectItem value="medium">Medium - Balanced</SelectItem>
                  <SelectItem value="high">High - Aggressive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Min/Max per Item */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min per Item</Label>
                <Input
                  type="number"
                  value={constraints.min_allocation_per_item}
                  onChange={(e) => setConstraints(prev => ({ ...prev, min_allocation_per_item: Number(e.target.value) }))}
                  data-testid="min-allocation"
                />
              </div>
              <div>
                <Label>Max per Item</Label>
                <Input
                  type="number"
                  value={constraints.max_allocation_per_item || ''}
                  onChange={(e) => setConstraints(prev => ({ ...prev, max_allocation_per_item: e.target.value ? Number(e.target.value) : null }))}
                  data-testid="max-allocation"
                />
              </div>
            </div>

            {/* Optimization Goal */}
            <div>
              <Label>Optimization Goal</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { value: 'balanced', label: 'Balanced', icon: BarChart3 },
                  { value: 'maximize_roi', label: 'Max ROI', icon: TrendingUp },
                  { value: 'minimize_risk', label: 'Min Risk', icon: Shield },
                  { value: 'maximize_benefit', label: 'Max Benefit', icon: Target }
                ].map((goal) => (
                  <Button
                    key={goal.value}
                    variant={optimizationGoal === goal.value ? 'default' : 'outline'}
                    size="sm"
                    className="justify-start"
                    onClick={() => setOptimizationGoal(goal.value)}
                    data-testid={`goal-${goal.value}`}
                  >
                    <goal.icon className="w-4 h-4 mr-2" />
                    {goal.label}
                  </Button>
                ))}
              </div>
            </div>

            <Button 
              className="w-full" 
              onClick={handleOptimize}
              disabled={optimizing}
              data-testid="optimize-btn"
            >
              {optimizing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              {optimizing ? 'Optimizing...' : 'Run Optimization'}
            </Button>
          </CardContent>
        </Card>

        {/* Results Panel */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Optimization Results
            </CardTitle>
            <CardDescription>
              {result ? 'AI-suggested allocation changes' : 'Run optimization to see results'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-center py-12 text-[var(--clr-text-secondary)]">
                <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">No optimization results yet</p>
                <p className="text-sm">Configure constraints and run the optimizer</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-[var(--clr-surface-subtle)] rounded-lg">
                    <p className="text-2xl font-bold">
                      {formatCurrency(result.summary?.total_allocated)}
                    </p>
                    <p className="text-xs text-[var(--clr-text-secondary)]">Total Allocated</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--clr-surface-subtle)] rounded-lg">
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(result.summary?.expected_benefit)}
                    </p>
                    <p className="text-xs text-[var(--clr-text-secondary)]">Expected Benefit</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--clr-surface-subtle)] rounded-lg">
                    <Badge 
                      variant={result.summary?.risk_level === 'low' ? 'default' : 'secondary'}
                      className="mb-1"
                    >
                      {result.summary?.risk_level || 'medium'}
                    </Badge>
                    <p className="text-xs text-[var(--clr-text-secondary)]">Risk Level</p>
                  </div>
                  <div className="text-center p-4 bg-[var(--clr-surface-subtle)] rounded-lg">
                    <p className="text-2xl font-bold">
                      {result.summary?.items_optimized || result.allocations?.length}
                    </p>
                    <p className="text-xs text-[var(--clr-text-secondary)]">Items Optimized</p>
                  </div>
                </div>

                {/* Explanation */}
                {result.explanation && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">AI Explanation</p>
                        <p className="text-sm text-blue-700 mt-1">{result.explanation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Allocations Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[var(--clr-surface-subtle)]">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Item</th>
                        <th className="text-right p-3 text-sm font-medium">Original</th>
                        <th className="text-right p-3 text-sm font-medium">Optimized</th>
                        <th className="text-right p-3 text-sm font-medium">Change</th>
                        <th className="text-center p-3 text-sm font-medium">Confidence</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {result.allocations?.slice(0, 10).map((alloc, idx) => (
                        <tr key={alloc.line_item_id || idx} className="hover:bg-[var(--clr-surface-subtle)]">
                          <td className="p-3">
                            <p className="font-medium text-sm">{alloc.target_name}</p>
                            {alloc.rationale && (
                              <p className="text-xs text-[var(--clr-text-secondary)] mt-1 line-clamp-1">
                                {alloc.rationale}
                              </p>
                            )}
                          </td>
                          <td className="p-3 text-right text-sm">
                            {formatCurrency(alloc.original_amount)}
                          </td>
                          <td className="p-3 text-right text-sm font-medium">
                            {formatCurrency(alloc.optimized_amount)}
                          </td>
                          <td className={`p-3 text-right text-sm ${getDeltaColor(alloc.delta)}`}>
                            {alloc.delta > 0 ? '+' : ''}{formatCurrency(alloc.delta)}
                            <span className="text-xs ml-1">({alloc.delta_percent}%)</span>
                          </td>
                          <td className="p-3 text-center">
                            <Badge variant="outline">
                              {Math.round((alloc.confidence || 0) * 100)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Apply Button */}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setResult(null)}>
                    <X className="w-4 h-4 mr-2" />
                    Discard
                  </Button>
                  <Button onClick={handleApply} disabled={applying}>
                    {applying ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Apply Optimization
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Optimization History</CardTitle>
            <CardDescription>Previous optimization runs for this scenario</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.slice(0, 5).map((opt) => (
                <div
                  key={opt.optimization_id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-[var(--clr-surface-subtle)]"
                >
                  <div className="flex items-center gap-3">
                    {getGoalIcon(opt.goal)}
                    <div>
                      <p className="font-medium capitalize">{opt.goal?.replace('_', ' ')}</p>
                      <p className="text-sm text-[var(--clr-text-secondary)]">
                        {opt.created_at ? new Date(opt.created_at).toLocaleString() : ''}
                        {' • '}
                        {opt.created_by}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={opt.ai_generated ? 'default' : 'secondary'}>
                      {opt.ai_generated ? 'AI' : 'Heuristic'}
                    </Badge>
                    <Badge variant="outline">
                      {Math.round((opt.confidence_score || 0) * 100)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScenarioOptimizerPage;
